import {detailedDiff} from 'deep-object-diff';
import {AudienceStatus} from '@gl/lit/common/constants/index.js';
import {
  BUILDER_PREFERENCES_KEY,
  LAYER_ROOT_KEY,
} from '@gl/lit/common/constants/builders/audience_builder.js';
import type {
  IClassicBuilderNode,
  IClassicBuilderEdge,
  ClassicBuilderConstructorArgs,
  IBuilderVersion,
} from '@gl/builder';
import {BuilderEdgeTypes} from '@gl/builder';
import {ChildEdge} from '@gl/lit/lib/builder/audience_builder/edges/child.js';
import {
  NeighborEdge,
  NeighborEdgeEvents,
} from '@gl/lit/lib/builder/audience_builder/edges/neighbor.js';
import {
  createExpressionFromType,
  FilterNode,
} from '@gl/lit/lib/builder/audience_builder/nodes/filter.js';
import type {AggregateExpression} from '@gl/lit/lib/builder/audience_builder/nodes/flex_filter.js';
import {FlexNode} from '@gl/lit/lib/builder/audience_builder/nodes/flex_filter.js';
import {SectionNode} from '@gl/lit/lib/builder/audience_builder/nodes/section.js';
import type {StemChildEventPayload} from '@gl/lit/lib/builder/audience_builder/nodes/stem.js';
import {
  FilterStem,
  StemEventTags,
  StemNode,
} from '@gl/lit/lib/builder/audience_builder/nodes/stem.js';
import {ClassicBuilder} from '@gl/builder/classic_builder/builder.js';
import type {ClassicEdgeUpdatePayload} from '@gl/builder/classic_builder/edge.js';
import {EdgeEventTags} from '@gl/builder/classic_builder/edge.js';
import type {ClassicNodeUpdatePayload} from '@gl/builder/classic_builder/node.js';
import {NodeEventTags} from '@gl/builder/classic_builder/node.js';
import type {IChannelMessage, IChannelSubscription} from '@gl/utils/channel.js';
import {ChannelMessage, ChannelMessageTag} from '@gl/utils/channel.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {
  Preference,
  PreferenceChannelTags,
  PreferenceManager,
} from '@gl/lit/lib/preference.js';
import type {
  Audience,
  AudienceTemplate,
  Tag,
  CustomAttributes,
} from '@gl/lit/types/audiences.js';
import {AudienceCategory, AudienceType} from '@gl/lit/types/audiences.js';
import type {
  ExpressionTypes,
  SerializedBreakdown,
  BuilderPreferenceValue,
  IAudienceBuilderQueryCtx,
} from '@gl/lit/types/builders/audience_builder/index.js';
import {
  AudienceBuilderContext,
  AudienceBuilderNodeTypes,
  AudienceBuilderView,
  NeighborOperators,
} from '@gl/lit/types/builders/audience_builder/index.js';
import type {
  Dataset,
  DatasetGroup,
  DatasetType,
  FlexDatasets,
  DatasetLabel,
  SourceConnection,
} from '@gl/lit/types/datasets.js';
import type {Team} from '@gl/lit/types/teams.js';
import {isUnsavedCopy} from '@gl/lit/utils/audiences.js';
import {defaultOperatorForType} from '@gl/lit/utils/audience_builder/edges.js';
import {
  isFilter,
  isFlex,
  isStem,
  isTextNode,
} from '@gl/lit/utils/audience_builder/filters.js';
import {
  filterStemConstraints,
  flexStemConstraints,
  isAudienceBuilderNode,
  isAudienceNode,
  isStemmableNode,
} from '@gl/lit/utils/audience_builder/nodes.js';
import {debounce} from '@gl/utils/debounce.js';
import {
  ABRequestKeys,
  ABRequestManager,
} from './request_manager/request_manager.js';
import type {QuerviceTableSchema} from '@gl/lit/types/quervice.js';
import {
  querviceService,
  QuerviceServiceSchemaBody,
} from '@gl/lit/services/quervice.js';
import {isUndefined, isNumber, isDefined} from '@gl/lit/utils/index.js';
import {renderErrorNotification} from '@gl/lit/utils/notifications.js';
import {abLocale} from '@gl/lit/common/locale/audience_builder.js';
import {locale} from '@gl/lit/common/locale/index.js';
import {isRight} from '@gl/lit/types/Either.js';
import {reviewAudienceService} from '@gl/lit/services/review_audiences.js';
import type {
  AudienceInsightsServerResponse,
  InsightsServiceResponse,
} from './services/insights_service.js';
import {InsightsService} from './services/insights_service.js';
import type {
  AudienceRequestBody,
  AudienceRequestReadOnlyBody,
} from './services/audience_service.js';
import {AudienceService} from './services/audience_service.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {AudienceNameConflictError} from './request_manager/errors.js';

// How long requests are debounce for.
export const DEBOUNCE_MS = 300;
/** Default for how many levels deep children can go. */
export const DEFAULT_NEST_LIMIT = Number.MAX_SAFE_INTEGER;
/**
 * Default treatment percentile.
 */
export const DEFAULT_TREATMENT = 0.7;
/**
 * Default control percentile.
 */
export const DEFAULT_CONTROL = 0.3;

export enum AudienceBuilderEventTags {
  INITIALIZED_BUILDER = 'initialized-builder',
  SCHEMA_FETCHED = 'schema-fetched',
  REQUEST_UPDATE = 'request-update',
  ADMIN_QUERY = 'admin-query',
  SAVE = 'save',
  UPDATE = 'update',
  CONTEXT_CHANGE = 'context-change',
  REPORT_OUT_OF_SYNC = 'report-out-of-sync',
  // Insights tags
  START_INSIGHTS = 'start-insights',
  TERMINATE_INSIGHTS = 'terminate-insights',
  FINISH_INSIGHTS = 'finish-insights',
  // Interaction tags
  NODE_FOCUSED = 'node-focused',
  NODE_DELETED = 'node-deleted',
  AUDIENCE_APPROVED = 'audience-approved',
  // error tag
  AUDIENCE_NAME_CONFLICT = 'audience-name-conflict',
}

export type AudienceBuilderConstructorArgs = ClassicBuilderConstructorArgs & {
  preventRequests?: boolean;
  nestLimit?: number;
};

export type AudienceBuilderEventTagValue = {
  [AudienceBuilderEventTags.REPORT_OUT_OF_SYNC]: AudienceInsights;
  [AudienceBuilderEventTags.FINISH_INSIGHTS]: {
    insights: AudienceInsights;
    constraints: AudienceInsightsConstraints;
  };
};

// Used to flag the current internal state of the builder.
enum InternalStateFlags {
  FETCHING_INSIGHTS,
  SAVING_OR_UPDATING,
  REQUESTING_APPROVAL,
  CREATING,
  STAGED_REQUEST,
  APPROVING,
}

export type AudienceRequestParams = {
  type?: AudienceType;
};

export type AudienceMutationBodyParams = {
  audience_type: AudienceType;
  status?: AudienceStatus;
};

export type AudienceInsightsMetadata = {
  updatedAt: Date;
  isUpToDate: boolean;
};

export type AudienceInsights = AudienceInsightsServerResponse &
  AudienceInsightsMetadata;

export const createAudienceInsights = (
  params: Partial<AudienceInsightsMetadata> & AudienceInsightsServerResponse
): AudienceInsights => {
  return {
    ...params,
    isUpToDate: params.isUpToDate ?? true,
    updatedAt: params.updatedAt ?? new Date(),
  };
};

export type AudienceInsightsConstraints = {
  exportSizeReached: boolean;
  reportSizeReached: boolean;
};

export type AudienceBuilderMutationParams = {
  operator?: NeighborOperators;
  datasetType?: DatasetType;
  level?: string;
  index?: number;
  parent?: IClassicBuilderNode;
};

export const DEFAULT_MUTATION_PARAMS = {};

const DEFAULT_AUDIENCE_BUILDER_QUERY_CTX: IAudienceBuilderQueryCtx = {
  runInsightsRequest: true,
  runSaveOrUpdateRequest: true,
};

const getAudienceBuilderContext = (ctx: IAudienceBuilderQueryCtx = {}) => {
  return Object.assign({}, DEFAULT_AUDIENCE_BUILDER_QUERY_CTX, ctx);
};

type Neighbors = {
  left: string;
  right: string;
};

export interface IAudienceBuilderConfig {
  enabledNodeTypes: Set<AudienceBuilderNodeTypes>;
  enableDatasetCreation: boolean;
}

export const audienceBuilderConfig: IAudienceBuilderConfig = {
  enabledNodeTypes: new Set([
    AudienceBuilderNodeTypes.AUDIENCE,
    AudienceBuilderNodeTypes.FILTER,
    AudienceBuilderNodeTypes.FLEX,
    AudienceBuilderNodeTypes.STEM,
    AudienceBuilderNodeTypes.SECTION,
    AudienceBuilderNodeTypes.GOOGLE_AD,
    AudienceBuilderNodeTypes.GOOGLE_DISPLAY_AD,
    AudienceBuilderNodeTypes.TYPEFACE_EMAIL,
  ]),
  enableDatasetCreation: true,
};

const createAudienceEditURL = (
  datasetGroup: DatasetGroup,
  audience: Audience | AudienceTemplate
): string => {
  /**
   * this maintains any query params that are present in the current URL.
   * This allows us to conditionally show different UI elements based
   * on the query params, e.g. ?landing=create-template
   */
  const currentQueryParams = new URLSearchParams(location.search);
  const queryString = () =>
    Array.from(currentQueryParams.keys()).length > 0
      ? `?${currentQueryParams.toString()}`
      : '';

  if (currentQueryParams.has('copy')) {
    currentQueryParams.delete('copy');
  }

  return `/v2/dataset_groups/${datasetGroup.id}/audiences/${
    audience.id
  }/edit${queryString()}`;
};

/**
 * Default builder preference value object.
 */
const DEFAULT_BUILDER_PREFERENCE_VALUE: BuilderPreferenceValue = {
  isAutoSaveEnabled: true,
  isAutoReportRefreshEnabled: true,
  limit: 0,
  sortBy: null,
  order: 'Descending',
};

/** Number of MS before setting the `initialized` flag to true. */
const INITIALIZATION_TIMEOUT_MS = 100;
/** MS until change events are processed for edges/nodes. */
const CHANGE_DEBOUNCE_MS = 100;

/**
 * Default insights constraints
 * Those constraints are used to enable/disable some features during runtime,
 * like disabling exports if the audience size is too small.
 */
const DEFAULT_AUDIENCE_INSIGHTS_CONSTRAINTS: AudienceInsightsConstraints = {
  exportSizeReached: false,
  reportSizeReached: false,
};

export class AudienceBuilder extends ClassicBuilder {
  /** Preference object for audience builder settings. */
  static preference: Preference<BuilderPreferenceValue> = new Preference(
    BUILDER_PREFERENCES_KEY,
    DEFAULT_BUILDER_PREFERENCE_VALUE,
    {readOnInit: true}
  );

  datasetGroup?: DatasetGroup;
  datasetGroupsCount = 1;
  datasets: Dataset[] = [];
  team?: Team;
  labels: Record<string, DatasetLabel[]> = {};
  sourceConnection?: SourceConnection;
  config: IAudienceBuilderConfig = audienceBuilderConfig;
  requestManager: ABRequestManager;
  view = AudienceBuilderView.DEFAULT;
  tableSchemata: Map<number, QuerviceTableSchema> = new Map();
  schema_fetched = false;
  isEditing = false;
  nestLimit = DEFAULT_NEST_LIMIT;
  category = AudienceCategory.STANDARD;

  /**
   * HTTP Services
   */
  insightsService = new InsightsService();
  audiencesService = new AudienceService();

  /**
   * Audience data
   */
  name = '';
  sql = '';
  audienceInsightsConstraints = DEFAULT_AUDIENCE_INSIGHTS_CONSTRAINTS;
  description = '';
  treatment = DEFAULT_TREATMENT;
  control = DEFAULT_CONTROL;
  status = AudienceStatus.DRAFT;
  breakdowns: SerializedBreakdown[] = [];
  associatedTeamTags: Tag[] = [];
  tags: Tag[] = [];
  customAttributes: CustomAttributes = {};

  // Interactions
  private _focusedNode?: IClassicBuilderNode;
  private _primarySectionId!: string;

  // Internal state
  private stateFlags: Set<InternalStateFlags> = new Set();

  /** The last version of the audience that was either saved or updated */
  private lastSavedVersion?: IBuilderVersion;

  /**
   * Initial builder data
   */
  private _context: AudienceBuilderContext = AudienceBuilderContext.LANDING;
  private _audience?: Audience | AudienceTemplate;
  private _insights?: AudienceInsights;
  private _initialized = false;
  private _preventRequests: boolean;

  private setInitialized = debounce((value: boolean) => {
    this._initialized = value;
  }, INITIALIZATION_TIMEOUT_MS);

  private debouncedEdgeHandler = debounce(
    this.edgeChangeHandler,
    CHANGE_DEBOUNCE_MS
  );

  constructor(args: AudienceBuilderConstructorArgs) {
    super(args);
    this.debugTools();

    this._preventRequests = args.preventRequests ?? false;

    const childrenSubscription: IChannelSubscription = {
      filter: () => true,
      handler: this.stemEventHandler.bind(this),
    };

    if (args.nestLimit) this.nestLimit = args.nestLimit;

    this.channel.subscribe([childrenSubscription]);
    this.requestManager = new ABRequestManager();
    this.nestLimit = DEFAULT_NEST_LIMIT;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  set initialized(value: boolean) {
    this.setInitialized(value);
  }

  get isDisplay(): boolean {
    return this.view === AudienceBuilderView.DISPLAY;
  }

  get focusedNode() {
    return this._focusedNode;
  }

  set focusedNode(node: IClassicBuilderNode | undefined) {
    this._focusedNode = node;

    const tag = ChannelMessageTag.create<
      string,
      IClassicBuilderNode | undefined
    >(AudienceBuilderEventTags.NODE_FOCUSED, node);
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  get primarySection(): SectionNode {
    return this.getNodeFromCache(this._primarySectionId) as SectionNode;
  }

  get context(): AudienceBuilderContext {
    return this._context;
  }

  set context(ctx: AudienceBuilderContext) {
    this._context = ctx;
    const tag = ChannelMessageTag.create<string, AudienceBuilderContext>(
      AudienceBuilderEventTags.CONTEXT_CHANGE,
      ctx
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  get insights(): AudienceInsights | undefined {
    return this._insights;
  }

  set insights(insights: AudienceInsights | undefined) {
    this._insights = insights;
    this.setAudienceConstraints(this._insights);
  }

  get audience(): Audience | AudienceTemplate | undefined {
    return this._audience;
  }

  set audience(audience: Audience | AudienceTemplate | undefined) {
    this._audience = audience;
    if (!isUndefined(audience)) this.category = audience.category;
    this.setAudienceConstraints(this._insights);

    if (this._audience) {
      this.setTitle(this._audience.name);
      this.description = this._audience.description;
      try {
        this.breakdowns = JSON.parse(this._audience.breakdowns || '[]');
      } catch (_e: unknown) {
        this.breakdowns = [];
      }
    }
  }

  get minimumExportSize() {
    return (
      this.audience?.minimum_audience_export_size ??
      this.datasetGroup?.settings?.minimum_audience_export_size ??
      0
    );
  }

  get minimumReportSize() {
    return (
      this.audience?.minimum_audience_report_size ??
      this.datasetGroup?.settings?.minimum_audience_report_size ??
      0
    );
  }

  // Tells us whether the state of the builder is safe to save.
  get isSafeToSave(): boolean {
    if (!this.initialized || !this.insights) return false;

    return (
      !(this.isRequestingApproval || this.isFetchingInsights) &&
      this.audienceInsightsConstraints.reportSizeReached &&
      (this.insights?.isUpToDate ?? true)
    );
  }

  get isSaving(): boolean {
    return this.stateFlags.has(InternalStateFlags.SAVING_OR_UPDATING);
  }

  get isCreating(): boolean {
    return this.stateFlags.has(InternalStateFlags.CREATING);
  }

  get hasStagedRequest(): boolean {
    return this.stateFlags.has(InternalStateFlags.STAGED_REQUEST);
  }

  get isRequestingApproval(): boolean {
    return this.stateFlags.has(InternalStateFlags.REQUESTING_APPROVAL);
  }

  get isFetchingInsights(): boolean {
    return this.stateFlags.has(InternalStateFlags.FETCHING_INSIGHTS);
  }

  private debugTools() {
    const graphComparison = {
      savedGraph: {},
      save: () => {
        graphComparison.savedGraph = JSON.parse(this.graphJSON());
      },
      compare: () => {
        const current = JSON.parse(this.graphJSON());
        // eslint-disable-next-line no-console -- Debugging tool
        console.log(detailedDiff(graphComparison.savedGraph ?? {}, current));
      },
    };
    const debugTool = {
      get: () => this,
      graphComparison,
    };
    window.debug.tool({audienceBuilder: debugTool});
  }

  private createMutationBody(
    params: AudienceMutationBodyParams
  ): AudienceRequestBody | AudienceRequestReadOnlyBody {
    if (this.isDisplay) {
      return {
        tags: this.tags,
        custom_attributes: this.customAttributes,
      } as AudienceRequestReadOnlyBody;
    }

    return {
      name: this.name,
      query: this.sql,
      description: this.description,
      tags: this.tags,
      custom_attributes: this.customAttributes,
      treatment: this.treatment,
      control: this.control,
      size: this.insights?.size ?? 0,
      last_refreshed_size_at: this.insights?.updatedAt,
      creator: 'user',
      revenue_touched: this.insights?.revenue ?? 0,
      status: this.audience?.status || this.status,
      criteria_graph: this.graphAsJSON(),
      origin_audience_id: this.audience?.origin_audience_id,
      breakdowns: JSON.stringify(this.breakdowns),
      category: this.category,
      ...params,
    } as AudienceRequestBody;
  }

  private stageMutationRequest() {
    this.stateFlags.add(InternalStateFlags.STAGED_REQUEST);
  }

  private setStateFlag(flag: InternalStateFlags) {
    this.stateFlags.add(flag);
    this.dispatchUpdateEvent();
  }

  private removeStateFlag(...flags: InternalStateFlags[]) {
    let shouldDispatchUpdateEvent = false;

    for (const flag of flags) {
      if (!this.stateFlags.has(flag)) continue;
      this.stateFlags.delete(flag);
      shouldDispatchUpdateEvent = true;
    }

    if (shouldDispatchUpdateEvent) this.dispatchUpdateEvent();
  }

  /**
   * Emits a delete event.
   */
  private emitNodeDeleteEvent(node: IClassicBuilderNode) {
    const tag = ChannelMessageTag.create<string, IClassicBuilderNode>(
      AudienceBuilderEventTags.NODE_DELETED,
      node
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  /**
   * Handles all change events whenever a builder preference is changed.
   */
  private subscribeToPreferenceChanges() {
    PreferenceManager.instance().subscribe(BUILDER_PREFERENCES_KEY, {
      filter: () => true,
      handler: (msg: IChannelMessage) => {
        if (!msg.hasTag(PreferenceChannelTags.CHANGE)) return;

        if (
          AudienceBuilder.preference.value.isAutoReportRefreshEnabled &&
          this.hasUnsavedChanges()
        ) {
          this.dispatchQuerviceRequests();
          return;
        }

        if (
          AudienceBuilder.preference.value.isAutoSaveEnabled &&
          (this.hasUnsavedChanges() || this.hasUnsavedTitle())
        ) {
          this.requestSaveOrUpdate({
            type: this.audience?.audience_type,
          });
        }
      },
    });
  }

  /**
   * Determines whether or not the builder should request to update instead
   * of save.
   */
  private shouldUpdate(): boolean {
    return !!(
      this.audience && !new URLSearchParams(location.search).has('copy')
    );
  }

  private emitSchemaFetched() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.SCHEMA_FETCHED,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private emitAudienceNameConflict() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.AUDIENCE_NAME_CONFLICT,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private populateReportData() {
    if (!this.audience) return;

    this.treatment = this.audience.treatment ?? DEFAULT_TREATMENT;
    this.control = this.audience.control ?? DEFAULT_CONTROL;
    this.insights = createAudienceInsights({
      size: this.audience.size,
      revenue: this.audience.revenue_touched ?? 0,
      updatedAt: new Date(
        this.audience.last_refreshed_size_at ?? this.audience.updated_at
      ),
    });
  }

  /**
   * Dispatches audience save or update events.
   */
  private dispatchAudienceEvent() {
    const key = this.audience
      ? AudienceBuilderEventTags.UPDATE
      : AudienceBuilderEventTags.SAVE;
    const tag = ChannelMessageTag.create<
      string,
      Audience | AudienceTemplate | undefined
    >(key, this.audience);
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private dispatchStartInsightsEvent() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.START_INSIGHTS,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private dispatchInsightsOutOfSync(params: AudienceInsights) {
    const tag = ChannelMessageTag.create<string, AudienceInsights>(
      AudienceBuilderEventTags.REPORT_OUT_OF_SYNC,
      params
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private dispatchTerminateInsightsEvent() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.TERMINATE_INSIGHTS,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private dispatchFinishInsightsEvent(
    params: AudienceBuilderEventTagValue[AudienceBuilderEventTags.FINISH_INSIGHTS]
  ) {
    const tag = ChannelMessageTag.create<
      string,
      AudienceBuilderEventTagValue[AudienceBuilderEventTags.FINISH_INSIGHTS]
    >(AudienceBuilderEventTags.FINISH_INSIGHTS, params);
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  private dispatchAdminQueryEvent(query: string) {
    const tag = ChannelMessageTag.create<string, string>(
      AudienceBuilderEventTags.ADMIN_QUERY,
      query
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }
  /**
   * Get target neighbors returns neighbors for a node not yet added to a level.
   */
  private async getTargetNeighbors(
    node: IClassicBuilderNode
  ): Promise<Neighbors> {
    const level = await this.getLevel(node.level);
    if (!level) throw Error('Invalid level for node.');

    return {
      left: level[node.index - 1],
      right: level[node.index],
    };
  }

  private async getNeighbors(node: IClassicBuilderNode): Promise<Neighbors> {
    const level = await this.getLevel(node.level);
    if (!level) throw Error('Invalid level for node.');

    return {
      left: level[node.index - 1],
      right: level[node.index + 1],
    };
  }

  private async edgeChangeHandler(message: IChannelMessage): Promise<void> {
    for (const tag of message.tags()) {
      switch (tag.key()) {
        case EdgeEventTags.UPDATE: {
          const payload =
            tag.value() as ClassicEdgeUpdatePayload<IClassicBuilderEdge>;
          if (payload.edge) this.putEdge(payload.edge);
          break;
        }

        case NeighborEdgeEvents.OPERATOR_CHANGE: {
          const payload =
            tag.value() as ClassicEdgeUpdatePayload<IClassicBuilderEdge>;
          if (!payload.edge) break;

          await this.putEdge(payload.edge);
          this.dispatchQuerviceRequests();
          break;
        }
      }
    }
  }

  protected async onEdgeChange(message: IChannelMessage): Promise<void> {
    this.debouncedEdgeHandler(message);
  }

  protected onNodeChange(message: IChannelMessage): void {
    for (const tag of message.tags()) {
      switch (tag.key()) {
        case NodeEventTags.UPDATE: {
          const payload =
            tag.value() as ClassicNodeUpdatePayload<IClassicBuilderNode>;
          this.putNode(payload.node);
          break;
        }
      }
    }
  }

  /**
   * Notifies all channels in the sink that the builder is initialized.
   */
  emitInitializedEvent() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.INITIALIZED_BUILDER,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);

    this.initialized = true;
  }

  async fetchFullSchema() {
    if (this.schema_fetched) return;

    try {
      if (!this.datasetGroup) {
        throw Error('Builder is missing a dataset group.');
      }

      // FIX_ME: We should review the await for this call, as this is slow.
      const schemata = await querviceService.fetchFullSchema(
        QuerviceServiceSchemaBody.DATASET_GROUP,
        this.datasetGroup.id,
        this.datasets,
        String(this.audience?.id)
      );

      this.schema_fetched = true;

      if (!schemata) {
        this.emitSchemaFetched();
        throw Error('Failed to get schemata from data warehouse.');
      }

      this.tableSchemata = schemata;
      this.emitSchemaFetched();
    } catch (err) {
      console.error(err);
      renderErrorNotification(
        Error(abLocale[locale.current].errorMessages.fullSchema)
      );
    }
  }

  setupDatasetGroup(datasetGroup: DatasetGroup) {
    this.requestManager.setDatasetGroup(datasetGroup);
    this.audiencesService.setup({datasetGroup});
    this.insightsService.setup({datasetGroup});
  }

  setupAudience(audience: Audience | AudienceTemplate) {
    this.requestManager.setAudience(audience);
    this.audiencesService.setup({audience});
    this.insightsService.setup({audience});
  }

  initializeDisplay() {
    if (this.datasetGroup) this.setupDatasetGroup(this.datasetGroup);

    if (this.audience) {
      if (!isUnsavedCopy(this.audience)) this.setupAudience(this.audience);
    }

    this.populateReportData();
    this.emitInitializedEvent();
    return;
  }

  async initialize(): Promise<void> {
    if (this.isDisplay) {
      this.initializeDisplay();
      return;
    }

    if (!this.audience) await this.createPrimarySection();
    if (this.datasetGroup) this.setupDatasetGroup(this.datasetGroup);

    if (this.audience) {
      // Ensure request manager has access to the audience data.
      if (!isUnsavedCopy(this.audience)) this.setupAudience(this.audience);
      this.populateReportData();
    }

    /**
     * Prevent a user from immediately leaving a page if they have unsaved
     * changes.
     */
    addEventListener('beforeunload', e => {
      if (!this.hasUnsavedChanges()) return;
      return (e.returnValue = true);
    });

    this.subscribeToPreferenceChanges();
    this.emitInitializedEvent();
  }

  async createPrimarySection(): Promise<SectionNode> {
    if (this.primarySection) return this.primarySection;

    const primarySection = Array.from(this.cache.nodes.values()).find(n => {
      if (n instanceof SectionNode) {
        return n.tags.has('primary-section');
      }
    });

    if (primarySection) {
      this._primarySectionId = primarySection.id;
      return this.primarySection;
    }

    const section = SectionNode.create({});
    section.makePrimary();
    await this.pushNode(section);
    this._primarySectionId = section.id;

    return this.primarySection;
  }

  primaryDataset(): Dataset | undefined {
    if (isUndefined(this.datasetGroup)) return undefined;

    return this.datasets.find(d => {
      return d.id === this.datasetGroup!.primary_dataset_id;
    });
  }

  updateStoreMetadata(
    mutation: (metadata: Record<string, unknown>) => Record<string, unknown>
  ) {
    this.store.metadata = mutation(this.store.metadata);
  }

  isValid(): boolean {
    return !!this.name && typeof this.insights !== 'undefined';
  }

  hasUnsavedTitle(): boolean {
    return (
      !this.currentVersion &&
      ((this.audience && isUnsavedCopy(this.audience)) || !this.audience) &&
      this.isValid()
    );
  }

  hasUnsavedChanges(): boolean {
    if (
      this.hasUnsavedTitle() &&
      !AudienceBuilder.preference.value.isAutoSaveEnabled
    ) {
      return true;
    }

    if (!this.currentVersion) return false;
    if (!this.lastSavedVersion) return this.isValid();

    return this.currentVersion.compare(this.lastSavedVersion) > 0;
  }

  setDatasetGroup(datasetGroup: DatasetGroup) {
    this.datasetGroup = datasetGroup;
    this.requestManager.setDatasetGroup(datasetGroup);
    this.audiencesService.setup({datasetGroup});
    this.insightsService.setup({datasetGroup});
    this.setAudienceConstraints(this._insights);
  }

  setDatasetGroupsCount(count: number) {
    this.datasetGroupsCount = count;
  }

  setAudience(audience: Audience) {
    this.audience = audience;
    this.category = audience.category;
    this.requestManager.setAudience(audience);
    this.audiencesService.setup({audience});
    this.insightsService.setup({audience});
  }

  setTitle(title: string) {
    this.name = title;
    this.dispatchUpdateEvent();
  }

  /**
   * The constraints are calculated against the Audience minimum values
   * If by any reason (creating a new Audience for example) we don't have it
   * we fallback those values to whatever is set on the dataset group.
   * Last, we set those values to zero, as they were not setup.
   */
  setAudienceConstraints(insights?: AudienceInsights): void {
    if (!insights) {
      return;
    }

    this.audienceInsightsConstraints = {
      exportSizeReached:
        isNumber(insights.size) && insights.size >= this.minimumExportSize,
      reportSizeReached:
        isNumber(insights.size) && insights.size >= this.minimumReportSize,
    };
  }

  setTags(tags: Tag[]) {
    this.tags = tags;
    this.dispatchUpdateEvent();
  }

  setCustomAttributes(customAttributes: CustomAttributes) {
    this.customAttributes = customAttributes;
    this.dispatchUpdateEvent();
  }

  getFieldDescription(datasetId: number, fieldName: string) {
    const table = this.tableSchemata.get(datasetId);
    if (!table) return;

    const field = table.fields.find(f => f.name === fieldName);
    if (!field) return;

    return field.description;
  }

  /**
   * Request handlers.
   */

  handleRequestInsightsSuccess(response: InsightsServiceResponse): void {
    if (response.sql) this.sql = response.sql;
    this.insights = createAudienceInsights(response.data);

    this.dispatchFinishInsightsEvent({
      insights: this.insights,
      constraints: this.audienceInsightsConstraints,
    });

    // We have a race condition when a new Audience is being created.
    // If insights take too much time to respond on the /new route,
    // we can get in a situation where we don't save the Audience automatically
    // Here we trigger it again in this case.
    if (
      AudienceBuilder.preference.value.isAutoSaveEnabled &&
      this.hasUnsavedChanges() &&
      !this.audience?.updated_at
    ) {
      this.requestSaveOrUpdate({
        type: this.audience?.audience_type,
      });
    }
  }

  handleRequestInsightsFail(): void {
    this.dispatchTerminateInsightsEvent();
  }

  async requestInsights(): Promise<void> {
    this.dispatchStartInsightsEvent();
    this.setStateFlag(InternalStateFlags.FETCHING_INSIGHTS);

    await this.insightsService.requestInsights(
      {
        graph: this.graphAsJSON(),
        audience_id: String(this.audience?.id),
      },
      {
        onSuccess: this.handleRequestInsightsSuccess.bind(this),
        onError: this.handleRequestInsightsFail.bind(this),
      }
    );

    this.removeStateFlag(InternalStateFlags.FETCHING_INSIGHTS);
  }

  async requestSaveOrUpdate(params?: AudienceRequestParams): Promise<boolean> {
    if (!this.isSafeToSave || this._preventRequests) {
      return Promise.resolve(false);
    }

    const key = this.shouldUpdate() ? ABRequestKeys.UPDATE : ABRequestKeys.SAVE;

    if (this.isCreating) {
      this.stageMutationRequest();
      return Promise.resolve(false);
    }

    if (!this.isValid()) {
      throw Error(`Cannot ${key} an invalid audience.`);
    }

    if (key === ABRequestKeys.SAVE) {
      this.setStateFlag(InternalStateFlags.CREATING);
    }

    this.setStateFlag(InternalStateFlags.SAVING_OR_UPDATING);

    const audienceType = params?.type ?? AudienceType.DEFAULT;
    const payload = this.createMutationBody({audience_type: audienceType});

    const response = await this.audiencesService.saveOrUpdate(
      {key, payload, options: {disallowAbort: this.isCreating}},
      {
        onSuccess: response => {
          this.setAudience(response.data.audience);
          this.dispatchAudienceEvent();
          if (
            key === ABRequestKeys.SAVE &&
            this.audience &&
            this.datasetGroup
          ) {
            FlywheelComponent.router.navigate(
              createAudienceEditURL(this.datasetGroup, this.audience)
            );
          }

          this.lastSavedVersion = this.currentVersion?.clone();

          if (this.hasStagedRequest) {
            this.requestSaveOrUpdate({
              type: this.audience?.audience_type,
            });
            this.stateFlags.delete(InternalStateFlags.STAGED_REQUEST);
          }
        },
        onError: error => {
          if (error.name === AudienceNameConflictError.errorName) {
            this.emitAudienceNameConflict();
          }
        },
      }
    );

    this.removeStateFlag(
      InternalStateFlags.SAVING_OR_UPDATING,
      InternalStateFlags.CREATING
    );

    return isRight(response);
  }

  async requestAudienceApproval(): Promise<void> {
    if (!this.isValid()) throw Error('Cannot update an invalid audience.');

    this.setStateFlag(InternalStateFlags.REQUESTING_APPROVAL);

    const body = this.createMutationBody({
      audience_type: this.audience?.audience_type ?? AudienceType.DEFAULT,
      status: AudienceStatus.PENDING_APPROVAL,
    });

    await this.audiencesService.requestApproval(body, {
      onSuccess: response => {
        this.setAudience(response.data.audience);
        this.dispatchAudienceEvent();
      },
      onError: () => {
        return;
      },
    });

    this.removeStateFlag(InternalStateFlags.REQUESTING_APPROVAL);
  }

  async approveAudience(params: {
    audienceIds: number[];
    formToken: string;
  }): Promise<void> {
    try {
      if (!this.isValid()) throw Error('Cannot approve an invalid audience.');
      this.setStateFlag(InternalStateFlags.APPROVING);
      await reviewAudienceService.approveAudiences(params);
      this.dispatchAudienceEvent();
    } catch (err) {
      console.error(err);
    } finally {
      this.removeStateFlag(InternalStateFlags.APPROVING);
    }
  }

  /**
   * Events
   * ======
   */

  dispatchUpdateEvent() {
    const tag = ChannelMessageTag.create<string, null>(
      AudienceBuilderEventTags.REQUEST_UPDATE,
      null
    );
    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
  }

  dispatchQuerviceRequests(forceRefresh = false) {
    if (this._preventRequests) return;

    const autoRefreshEnabled =
      AudienceBuilder.preference.value.isAutoReportRefreshEnabled;

    if (autoRefreshEnabled || forceRefresh) {
      this.requestInsights().then(() => {
        // Save the audience
        if (
          AudienceBuilder.preference.value.isAutoSaveEnabled &&
          this.isValid()
        ) {
          this.requestSaveOrUpdate({
            type: this.audience?.audience_type,
          });
        }
      });
      return;
    }

    const insights = this.insights ?? {
      size: null,
      revenue: null,
    };
    this.insights = createAudienceInsights({
      ...insights,
      isUpToDate: false,
    });

    this.dispatchInsightsOutOfSync(this.insights);
  }

  /**
   * Node mutation methods
   * =====================
   */
  async putNode(
    node: IClassicBuilderNode,
    context?: IAudienceBuilderQueryCtx
  ): Promise<void> {
    if (this.isDisplay) return;

    const ctx = getAudienceBuilderContext(context);

    try {
      await super.putNode(node, ctx);
      if (
        isFilter(node) &&
        isAudienceBuilderNode(node) &&
        node.isValid() &&
        ctx.runInsightsRequest
      ) {
        this.dispatchQuerviceRequests();
      }

      if (
        isTextNode(node) &&
        !this.isFetchingInsights &&
        ctx.runSaveOrUpdateRequest &&
        this.isValid()
      ) {
        this.requestSaveOrUpdate({
          type: this.audience?.audience_type,
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  async unshiftChild(
    parentKey: string,
    child: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<void> {
    try {
      if (child.level !== parentKey) {
        throw Error('Child level is not set to its parent.');
      }

      const hierarchy = await this.getLevel(parentKey);
      const childIdx = 0;
      const edge = ChildEdge.create(parentKey, child.id, this);
      await this.putEdge(edge);

      if (!hierarchy || hierarchy.length === 0) {
        const [neighborId] = hierarchy ?? [];
        const edge = NeighborEdge.create(child.id, neighborId, this);
        const neighbor = await this.getNode(neighborId);
        if (!neighbor) throw Error('Invalid node id within hierarchy');

        if (
          neighbor.type === AudienceBuilderNodeTypes.FILTER &&
          child.type === AudienceBuilderNodeTypes.FILTER
        ) {
          edge.operator = params.operator;
        }

        await this.putEdge(edge);
      }

      child.index = childIdx;
      await this.putLevel(child.level, level => {
        level.unshift(child.id);
        return level;
      });
    } catch (err) {
      console.error(err);
    }
  }

  async putNeighborNode(
    node: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<void> {
    try {
      await this.putNode(node, {runInsightsRequest: false});
      const neighbors = await this.getTargetNeighbors(node);

      if (neighbors.left) {
        const edge = NeighborEdge.create(neighbors.left, node.id, this);
        const neighbor = this.getNodeFromCache(neighbors.left);
        if (!neighbor) throw Error('Invalid node id within hierarchy');

        if (isStemmableNode(neighbor) && isStemmableNode(node)) {
          edge.operator =
            params.operator ||
            defaultOperatorForType(node.type as AudienceBuilderNodeTypes);
        }

        await this.putEdge(edge);
      }

      if (neighbors.right) {
        const edge = NeighborEdge.create(node.id, neighbors.right, this);
        const neighbor = this.getNodeFromCache(neighbors.right);
        if (!neighbor) throw Error('Invalid node id within hierarchy');

        if (isStemmableNode(neighbor) && isStemmableNode(node)) {
          edge.operator =
            params.operator ||
            defaultOperatorForType(neighbor.type as AudienceBuilderNodeTypes);
        }

        await this.putEdge(edge);
      }

      await this.putLevel(node.level, level => {
        level.splice(node.index, 0, node.id);
        return level;
      });

      if (
        (isStemmableNode(node) || isAudienceNode(node)) &&
        isAudienceBuilderNode(node) &&
        node.isValid()
      ) {
        this.dispatchQuerviceRequests();
      }
    } catch (err) {
      console.error(err);
    }
  }

  stemEventHandler(message: IChannelMessage) {
    for (const tag of message.tags()) {
      switch (tag.key()) {
        case StemEventTags.ADD_CHILD: {
          const payload =
            tag.value() as StemChildEventPayload<IClassicBuilderNode>;
          const params = {
            operator:
              payload.operator ||
              defaultOperatorForType(
                payload.node.type as AudienceBuilderNodeTypes
              ),
          };

          if (payload.node instanceof StemNode) {
            payload.node.datasetTypeConstraint =
              payload.stem.datasetTypeConstraint;
          }

          if (payload.node instanceof SectionNode) {
            payload.node.datasetTypeConstraint =
              payload.stem.datasetTypeConstraint;
          }

          this.addChildNode(payload.stem.id, payload.node, params);
        }
      }
    }
  }

  async addChildNode(
    parentKey: string,
    child: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ) {
    try {
      if (child.level !== parentKey) {
        throw Error('Child level is not set to its parent.');
      }

      await this.insertNode(child, child.index, params);
    } catch (err) {
      console.error(err);
    }
  }

  async insertNode(
    node: IClassicBuilderNode,
    index: number,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<void> {
    try {
      node.index = index;
      await this.putNeighborNode(node, params);

      const isValidChild =
        node.level === LAYER_ROOT_KEY ||
        (await this.hasEdge(node.level, node.id, BuilderEdgeTypes.CHILD));

      if (!isValidChild) {
        const edge = ChildEdge.create(node.level, node.id, this);
        await this.putEdge(edge);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async pushNode(
    node: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<void> {
    try {
      const level = await this.getLevel(node.level);
      if (!level) throw Error('Invalid node level.');
      await this.insertNode(node, level.length, params);
    } catch (err) {
      console.error(err);
    }
  }

  async unshiftNode(
    node: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<void> {
    try {
      const level = await this.getLevel(node.level);
      if (!level) throw Error('Invalid node level.');
      await this.insertNode(node, 0, params);
    } catch (err) {
      console.error(err);
    }
  }

  // eslint-disable-next-line complexity
  async createFilterStem(
    preceedingNode: IClassicBuilderNode,
    targetNode: IClassicBuilderNode,
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ) {
    try {
      if (preceedingNode.level !== targetNode.level) {
        throw Error(
          'While attempting to make a filter stem, the node levels did not match.'
        );
      }

      const targetType = isFlex(targetNode)
        ? AudienceBuilderNodeTypes.FLEX
        : AudienceBuilderNodeTypes.FILTER;

      const constraints = Array.from(
        targetType !== AudienceBuilderNodeTypes.FLEX
          ? filterStemConstraints(targetNode, this, {
              nestLimit: this.nestLimit,
            })
          : flexStemConstraints(targetNode, this, {
              nestLimit: this.nestLimit,
            })
      );

      const stem = FilterStem.create({
        builder: this,
        index: preceedingNode.index,
        level: params.parent?.id ?? preceedingNode.level,
        operator: params.operator,
        datasetType: params.datasetType,
        constraints,
      });

      const section = SectionNode.create({
        level: stem.id,
        datasetTypeConstraint: params.datasetType,
        typeConstraints: constraints,
      });

      // Makes the section primary for the stem.
      section.makePrimary();
      section.defaultNodeType = section.currentNodeType = targetType;

      const preceedingEdges = await this.getEdgesForNode(preceedingNode.id);
      const targetEdges = await this.getEdgesForNode(targetNode.id);
      if (!preceedingEdges || !targetEdges) {
        throw Error('Failed to fetch preceeding and target edges.');
      }

      for (const edge of [...preceedingEdges, ...targetEdges]) {
        if (
          edge.type === BuilderEdgeTypes.CHILD &&
          edge.source() !== targetNode.id &&
          edge.source() !== preceedingNode.id
        ) {
          this.removeEdge(edge.id);

          const source = this.getNodeFromCache(edge.source());
          if (source && isStem(source)) {
            (source as StemNode).removeNodeId(edge.target());
          }
        }

        if (
          edge.target() !== targetNode.id &&
          edge.source() !== preceedingNode.id
        ) {
          // FIXME(joshua): Temporarily leaving this commented out.
          // if (edge instanceof NeighborEdge) edge.operator = params.operator;
          continue;
        }

        if (edge.type === BuilderEdgeTypes.FORWARD) this.removeEdge(edge.id);
      }

      await this.putLevel(preceedingNode.level, level => {
        level = level.filter(
          id => ![preceedingNode.id, targetNode.id].includes(id)
        );

        return level;
      });

      const parent = this.getNodeFromCache(stem.level);
      const parentParams = {
        operator: isStem(parent) ? parent.operatorConstraint : undefined,
      };
      if (parent && isStem(parent)) {
        parent.registerNode(stem);
        await this.addChildNode(stem.level, stem, parentParams);
      } else {
        await this.putNeighborNode(stem, parentParams);
      }

      for (const node of [preceedingNode, targetNode, section]) {
        node.level = stem.id;
        await this.pushNode(node, params);
        stem.registerNode(node);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async flattenStem(stem: StemNode): Promise<void> {
    try {
      const level = (await this.getLevel(stem.id)) ?? [];
      const nodes = level
        .map(id => stem.nodes.get(id) ?? this.getNodeFromCache(id))
        .filter(isDefined);
      const neighbors = await this.getNeighbors(stem);
      const sections = nodes.filter(
        n => n.type === AudienceBuilderNodeTypes.SECTION
      );

      await this.removeNode(stem.id);
      await this.removeLevel(stem.id);
      await Promise.all(sections.map(s => this.removeNode(s.id)));
      await this.putLevel(stem.level, level => {
        return level.filter(nid => nid !== stem.id);
      });

      const root = [...((await this.getLevel(stem.level)) || [])];
      const filteredNodes = nodes.filter(
        n => n.type !== AudienceBuilderNodeTypes.SECTION
      );

      for (let i = 0; i < filteredNodes.length; i++) {
        const node = filteredNodes[i];
        const neighbor = filteredNodes[i + 1];
        const levelNode = this.getNodeFromCache(stem.level);
        const rightIdx = root.findIndex(nid => nid === neighbors.right);
        const leftIdx = root.findIndex(nid => nid === neighbors.left);
        const startingIdx = (() => {
          if (leftIdx) return leftIdx + 1;
          if (rightIdx) return rightIdx;
          return root.length;
        })();

        if (neighbor && isStemmableNode(neighbor)) {
          const edge = this.getEdgeFromCache(
            node.id,
            neighbor.id,
            BuilderEdgeTypes.FORWARD
          );

          if (edge) {
            (edge as NeighborEdge).operator =
              levelNode && isStem(levelNode)
                ? levelNode.operatorConstraint
                : defaultOperatorForType(node.type as AudienceBuilderNodeTypes);
            await this.putEdge(edge);
          }
        }

        node.level = stem.level;
        if (levelNode && isStem(levelNode)) {
          levelNode.registerNode(node);
          levelNode.removeNodeId(stem.id);
          node.index = startingIdx + i;

          await this.addChildNode(levelNode.id, node, {
            index: startingIdx + i,
            operator: levelNode.operatorConstraint,
          });
        } else {
          await this.insertNode(node, startingIdx + i);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  async deleteNode(node: IClassicBuilderNode): Promise<void> {
    try {
      const parent = this.getNodeFromCache(node.level);

      if (parent && isStem(parent)) {
        (parent as StemNode).removeNode(node);
      }

      const neighbors = await this.getNeighbors(node);
      await this.removeNode(node.id);

      if (neighbors.left && neighbors.right) {
        const edge = NeighborEdge.create(neighbors.left, neighbors.right, this);
        const left = this.getNodeFromCache(neighbors.left);
        const right = this.getNodeFromCache(neighbors.right);
        if (!left || !right) throw Error('Invalid cache for audience graph.');

        if (isFilter(left) && isFilter(right)) {
          edge.operator = defaultOperatorForType(
            right.type as AudienceBuilderNodeTypes
          );
        }

        await this.putEdge(edge);
      }

      this.dispatchQuerviceRequests();
      // Dispatches relevant delete event.
      this.emitNodeDeleteEvent(node);
    } catch (err) {
      console.error(err);
    }
  }

  async getUpLevelNodes(node: IClassicBuilderNode) {
    const edges = await this.store.getEdgesForNode(node.id);
    if (!edges.ok) return [];
    return edges.data
      .filter(edge => {
        return edge instanceof ChildEdge && edge.target() === node.id;
      })
      .map(edge => this.getNodeFromCache(edge.source()));
  }

  async getParentNode(
    node: IClassicBuilderNode
  ): Promise<IClassicBuilderNode | null> {
    if (node.level === LAYER_ROOT_KEY) return null;
    return (await this.getNode(node.level)) ?? null;
  }

  async _isSimpleOrRelation(
    node: Nullable<IClassicBuilderNode>
  ): Promise<boolean> {
    if (!node) return false;
    const parentNode = (await this.getParentNode(node)) as StemNode;
    if (parentNode?.type !== AudienceBuilderNodeTypes.STEM) return false;
    const edges = await this.store.getEdgesForNode(parentNode.id);
    if (!edges.ok) return false;
    const childEdges = edges.data.filter(
      edge => edge instanceof ChildEdge && edge.source() === parentNode.id
    );
    return (
      // The STEM has 2 flex nodes + the section node "Start typing to add ..."
      childEdges.length === 3 &&
      (parentNode.operatorConstraint === NeighborOperators.OR ||
        parentNode.operatorConstraint === NeighborOperators.XOR)
    );
  }

  async _replaceNode(
    node: IClassicBuilderNode,
    replacement: IClassicBuilderNode
  ): Promise<void> {
    try {
      const neighbors = await this.getNeighbors(node);

      await this.putLevel(node.level, level => {
        level.splice(node.index, 1, replacement.id);
        return level;
      });

      Object.assign(replacement, {
        index: node.index,
        level: node.level,
      });

      await this.putNode(replacement, {
        runInsightsRequest: false,
        runSaveOrUpdateRequest: false,
      });

      await this.removeNode(node.id);

      if (neighbors.left) {
        const edge = NeighborEdge.create(neighbors.left, replacement.id, this);
        const left = this.getNodeFromCache(neighbors.left);
        if (left && isFilter(left)) {
          edge.operator = defaultOperatorForType(
            replacement.type as AudienceBuilderNodeTypes
          );
        }

        await this.putEdge(edge);
      }

      if (neighbors.right) {
        const edge = NeighborEdge.create(replacement.id, neighbors.right, this);
        const right = this.getNodeFromCache(neighbors.right);

        if (right && isFilter(right)) {
          edge.operator = defaultOperatorForType(
            right.type as AudienceBuilderNodeTypes
          );
        }

        await this.putEdge(edge);
      }

      this.dispatchQuerviceRequests();
    } catch (err) {
      console.error(err);
    }
  }

  async deleteStem(stem: StemNode): Promise<void> {
    try {
      const parentNode = (await this.getParentNode(stem)) as Nullable<StemNode>;

      const hasOrConstraint =
        parentNode?.operatorConstraint === NeighborOperators.OR ||
        parentNode?.operatorConstraint === NeighborOperators.XOR;
      const neighbors = await this.getNeighbors(stem);
      await this.purgeNode(stem);

      if (neighbors.left && neighbors.right) {
        const edge = NeighborEdge.create(neighbors.left, neighbors.right, this);
        const left = this.getNodeFromCache(neighbors.left);
        const right = this.getNodeFromCache(neighbors.right);
        if (!left || !right) throw Error('Invalid cache for audience graph.');

        if (isFilter(left) && isFilter(right)) {
          edge.operator = hasOrConstraint
            ? parentNode.operatorConstraint
            : defaultOperatorForType(right.type as AudienceBuilderNodeTypes);
        }

        await this.putEdge(edge);
      }

      this.dispatchQuerviceRequests();
    } catch (err) {
      console.error(err);
    }
  }

  async addFlexNode(
    {
      expression,
      datasetId,
    }: {
      expression?: AggregateExpression;
      datasetId?: number;
    },
    params: AudienceBuilderMutationParams = DEFAULT_MUTATION_PARAMS
  ): Promise<FlexNode | undefined> {
    try {
      const parent = this.getNodeFromCache(params.level ?? '');
      const node = FlexNode.create({
        builder: this,
        expression,
        datasetId,
        datasetType: params.datasetType as FlexDatasets | undefined,
        level: params.level,
        index: params.index,
        operator: params.operator,
      });
      const section = SectionNode.create({
        level: node.id,
        datasetTypeConstraint: expression?.datasetType || params.datasetType,
        typeConstraints: [...node.nodeTypeConstraint],
      });

      // Makes the section primary for the stem.
      section.makePrimary();

      if (isStem(parent)) {
        parent.registerNode(node);
        await this.addChildNode(parent.id, node, params);
      } else {
        await this.insertNode(node, node.index, params);
      }

      await this.addChildNode(node.id, section);

      const constraints = Array.from(
        filterStemConstraints(parent ?? node, this, {
          nestLimit: this.nestLimit,
        })
      );

      node.addConstraints(constraints);
      section.nodeTypeConstraints = new Set(constraints);

      if (node.dataset(this)) {
        await this.addDefaultFields({
          level: node.id,
          parent: node,
          sectionIndex: section.index,
          dataset: node.dataset(this)!,
        });
      }

      return node;
    } catch (err) {
      console.error(err);
    }
  }

  async addDefaultFields({
    level,
    parent,
    sectionIndex,
    dataset,
  }: {
    level: string;
    parent?: IClassicBuilderNode;
    sectionIndex: number;
    dataset: Dataset;
  }): Promise<void> {
    try {
      if (!dataset.default_field_objects) return;

      const fieldObjects = dataset.default_field_objects
        .map(obj => Object.values(obj))
        .flat();

      let previousFilter: undefined | FilterNode;
      for (const field of fieldObjects) {
        const index = previousFilter ? previousFilter.index + 1 : sectionIndex;
        const filter = FilterNode.create({
          level,
          index,
        });

        const expression = createExpressionFromType(
          field.type as ExpressionTypes,
          field.id,
          field.display_name
        );

        filter.addExpression(expression);

        if (parent && isStem(parent)) {
          (parent as StemNode).registerNode(filter);
        }

        await this.insertNode(filter, index);

        previousFilter = filter;
      }
    } catch (err) {
      console.error(err);
    }
  }
}
